import { Alert, Button, Checkbox, Input, Modal, Spin } from "@arco-design/web-react";
import { GitBranch, RotateCcw, Search } from "lucide-react";
import { useRef, useState } from "react";

import { createUuid } from "../../../lib/trace/context";
import {
  getItineraryProfessionalSettings,
  reorderItineraryProfessionals,
  replaceItineraryProfessional,
  setItineraryProfessionalSettings,
} from "../service";
import type { ItineraryEditor, MajorCandidate, MajorCandidateQuery } from "../types";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";

type Props = {
  editor: ItineraryEditor;
  onChange: (editor: ItineraryEditor) => void;
  onAnnounce: (message: string) => void;
  listMajorCandidates?: (query?: MajorCandidateQuery) => Promise<MajorCandidate[]>;
  onDraftStateChange?: (dirty: boolean) => void;
};

/** 设置直接作用于整份行程，节点与交通仍由普通编辑器处理。 */
export function ItineraryProfessionalSettings({ editor, onChange, onAnnounce, listMajorCandidates, onDraftStateChange }: Props) {
  const settings = getItineraryProfessionalSettings(editor);
  const [visible, setVisible] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const [selected, setSelected] = useState<Array<{ id: string; name: string }>>([]);
  const [replacements, setReplacements] = useState<Array<{ sourceId: string; target: { id: string; name: string } }>>([]);
  const [mainMajorId, setMainMajorId] = useState("");
  const [candidates, setCandidates] = useState<Array<{ id: string; name: string }>>([]);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const trigger = useRef<HTMLElement | null>(null);

  const search = async (value = keyword) => {
    const request = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const results = await listMajorCandidates?.({ keyword: value.trim() || undefined, limit: 50 }) ?? [];
      if (request === generation.current) setCandidates(results);
    } catch (reason) {
      if (request === generation.current) setError(reason instanceof Error ? reason.message : "专业查询失败，请重试");
    } finally {
      if (request === generation.current) setLoading(false);
    }
  };
  const close = () => {
    generation.current += 1;
    setVisible(false);
    onDraftStateChange?.(false);
  };
  const choices = [...new Map([...selected, ...candidates].map((major) => [major.id, major])).values()];
  const valid = selected.length >= 1 && selected.length <= 5 && selected.some((major) => major.id === mainMajorId);
  const replacementSources = new Set(replacements.map((item) => item.sourceId));
  const removedMajors = settings?.majors.filter((major) => !selected.some((item) => item.id === major.id) && !replacementSources.has(major.id)) ?? [];
  const moveSelected = (index: number, offset: -1 | 1) => {
    const targetIndex = index + offset;
    if (targetIndex < 1 || targetIndex >= selected.length) return;
    setSelected((current) => {
      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
    onDraftStateChange?.(true);
  };
  const replaceSelected = (sourceId: string, targetId: string) => {
    const target = choices.find((major) => major.id === targetId);
    if (!target || selected.some((major) => major.id === target.id)) {
      setError("替换目标专业已存在或不可用");
      return;
    }
    setSelected((current) => current.map((major) => major.id === sourceId ? target : major));
    setMainMajorId((current) => current === sourceId ? target.id : current);
    setReplacements((current) => [...current.filter((item) => item.sourceId !== sourceId), { sourceId, target }]);
    onDraftStateChange?.(true);
  };
  const save = () => {
    try {
      let nextEditor = editor;
      replacements.forEach(({ sourceId, target }) => {
        nextEditor = replaceItineraryProfessional(nextEditor, sourceId, target);
      });
      const nextSettings = getItineraryProfessionalSettings(nextEditor);
      const selectedIds = new Set(selected.map((major) => major.id));
      const metadataOnly = Boolean(nextSettings)
        && nextSettings.majorIds.length === selected.length
        && selectedIds.size === selected.length
        && nextSettings.majorIds.every((id) => selectedIds.has(id));
      onChange(metadataOnly
        ? reorderItineraryProfessionals(nextEditor, selected.map((major) => major.id))
        : setItineraryProfessionalSettings(nextEditor, { mainMajorId, majors: selected }, createUuid));
      setConfirmRemoval(false);
      close();
      onAnnounce("专业设置已更新");
    } catch (reason) {
      setConfirmRemoval(false);
      setError(reason instanceof Error ? reason.message : "专业设置保存失败");
    }
  };

  return <>
    <Button size="small" icon={<GitBranch size={15} />} title="专业轨道设置" aria-label="专业轨道设置"
      onClick={(event) => {
        if (event.currentTarget instanceof HTMLElement) trigger.current = event.currentTarget;
        setSelected(settings?.majors ?? []);
        setReplacements([]);
        setMainMajorId(settings?.mainMajorId ?? "");
        setKeyword("");
        setError("");
        setVisible(true);
        void search("");
      }} />
    <Modal visible={visible} title="专业轨道设置" closeIcon={<ItineraryDialogCloseButton label="关闭专业轨道设置" />}
      okText="保存专业设置" cancelText="取消" okButtonProps={{ disabled: !valid }} onCancel={close}
      afterClose={() => trigger.current?.focus()}
      onOk={() => {
        if (!valid) return;
        const hasRemovedRoutes = editor.days.some((day) => day.parallelTracks?.tracks.some((track) => removedMajors.some((major) => major.id === track.majorId)));
        if (hasRemovedRoutes) setConfirmRemoval(true);
        else save();
      }}>
      <div className="itinerary-professional-settings">
        <div className="itinerary-professional-search">
          <Input aria-label="搜索专业" placeholder="搜索专业" maxLength={100} value={keyword} onChange={setKeyword} onPressEnter={() => void search()} />
          <Button icon={<Search size={16} />} aria-label="查询专业" title="查询专业" onClick={() => void search()} />
        </div>
        {error ? <Alert type="error" content={error} /> : null}
        <div className="itinerary-professional-order" role="list" aria-label="专业顺序">
          {selected.map((major, index) => <div className="itinerary-professional-order-row" key={major.id} role="listitem">
            <span className="itinerary-professional-order-name">{index + 1}. {major.name}</span>
            <button type="button" className="itinerary-professional-order-button" disabled={index <= 1}
              aria-label={`上移专业 ${major.name}`} onClick={() => moveSelected(index, -1)}>上移</button>
            <button type="button" className="itinerary-professional-order-button" disabled={index === 0 || index === selected.length - 1}
              aria-label={`下移专业 ${major.name}`} onClick={() => moveSelected(index, 1)}>下移</button>
            <select className="itinerary-professional-replace" aria-label={`更换专业 ${major.name}`} value=""
              onChange={(event) => replaceSelected(major.id, event.target.value)}>
              <option value="">更换专业</option>
              {choices.filter((candidate) => candidate.id !== major.id && !selected.some((item) => item.id === candidate.id)).map((candidate) => (
                <option key={candidate.id} value={candidate.id}>{candidate.name}</option>
              ))}
            </select>
          </div>)}
        </div>
        <Spin loading={loading}>
          <div className="itinerary-professional-choices" role="group" aria-label="行程专业">
            {choices.map((major) => <Checkbox key={major.id} checked={selected.some((item) => item.id === major.id)}
              disabled={selected.length >= 5 && !selected.some((item) => item.id === major.id)}
              onChange={(checked) => {
                const next = checked ? [...selected, major] : selected.filter((item) => item.id !== major.id);
                setSelected(next);
                if (!next.some((item) => item.id === mainMajorId)) setMainMajorId(next[0]?.id ?? "");
                onDraftStateChange?.(true);
              }}>{major.name}</Checkbox>)}
          </div>
        </Spin>
        <label className="itinerary-professional-primary">主专业
          <select aria-label="主专业" value={mainMajorId} disabled={!selected.length}
            onChange={(event) => {
              const nextMainId = event.target.value;
              setMainMajorId(nextMainId);
              setSelected((current) => [current.find((major) => major.id === nextMainId)!, ...current.filter((major) => major.id !== nextMainId)]);
              onDraftStateChange?.(true);
            }}>
            {!selected.length ? <option value="">请选择专业</option> : null}
            {selected.map((major) => <option key={major.id} value={major.id}>{major.name}</option>)}
          </select>
        </label>
        <span>{selected.length} / 5 个专业{selected.length < 1 ? "，至少选择 1 个" : ""}</span>
        {settings ? <Button status="danger" icon={<RotateCcw size={15} />} onClick={() => setConfirmDisable(true)}>回退普通编辑</Button> : null}
      </div>
    </Modal>
    <Modal visible={confirmRemoval} title="删除专业路线" closeIcon={<ItineraryDialogCloseButton label="关闭删除专业确认" />}
      okText="确认删除专业" cancelText="取消" okButtonProps={{ status: "danger" }} onCancel={() => setConfirmRemoval(false)} onOk={save}>
      确认删除“{removedMajors.map((major) => major.name).join("、") }”的独占点位、路线和交通设置？公共点位及保留专业的路线不受影响。
    </Modal>
    <Modal visible={confirmDisable} title="回退普通编辑" closeIcon={<ItineraryDialogCloseButton label="关闭回退确认" />}
      okText="确认回退" cancelText="取消" okButtonProps={{ status: "danger" }} onCancel={() => setConfirmDisable(false)}
      onOk={() => {
        try {
          onChange(setItineraryProfessionalSettings(editor, null, createUuid));
          setConfirmDisable(false);
          close();
          onAnnounce("已保留主专业并恢复普通编辑");
        } catch (reason) {
          setConfirmDisable(false);
          setError(reason instanceof Error ? reason.message : "回退失败");
        }
      }}>
      回退后仅保留主专业“{settings?.majors.find((major) => major.id === settings.mainMajorId)?.name}”及公共点位，其他专业的点位、路线和交通设置将被删除，无法恢复。
    </Modal>
  </>;
}
